/**
 * Canonical AI Tools grid definitions — single source of truth for:
 * rendered cards, total count, search, category filtering, and routes.
 */

export type AiToolCategoryKey =
  | 'catImages' |'catVideo' |'catAnimation' |'catVoice' |'catCharacters' |'catWriting' |'catSocial' |'catMarketing' |'catUtility';

export type AiToolLocaleBucket = Record<string, string | undefined> | undefined;

export interface AiToolDefinition {
  id: string;
  route: string;
  categoryKey: AiToolCategoryKey;
  categoryFallback: string;
  titleKey: string;
  titleFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  benefitKeys: [string, string, string];
  benefitFallbacks: [string, string, string];
  useCaseKeys: [string, string, string];
  useCaseFallbacks: [string, string, string];
  /** Always-on searchable terms (EN + PT + ES) so keyword search works in any UI language. */
  searchKeywords: string[];
  /** Emoji mark for most tools; unused when lucideIcon is set. */
  emoji: string;
  /** Optional Lucide icon name for tools that need a vector/file mark. */
  lucideIcon?: 'FileCode';
  color: string;
  bg: string;
  border: string;
  popular: boolean;
  badge: 'new' | 'popular' | null;
}

export interface AiToolCard {
  id: string;
  route: string;
  title: string;
  category: string;
  badge: string | null;
  description: string;
  benefits: string[];
  useCases: string[];
  searchKeywords: string[];
  emoji: string;
  lucideIcon?: 'FileCode';
  color: string;
  bg: string;
  border: string;
  popular: boolean;
}

/** Runtime card metadata — the only tool list the grid may render. */
export const AI_TOOLS: readonly AiToolDefinition[] = [
  {
    id: 'tool-image-prompt',
    route: '/create-page',
    categoryKey: 'catImages',
    categoryFallback: 'Images',
    titleKey: 'toolImagePromptTitle',
    titleFallback: 'Image Prompt Builder',
    descriptionKey: 'toolImagePromptDesc',
    descriptionFallback:
      'Craft ultra-detailed image prompts for Midjourney, DALL-E 3, Stable Diffusion, and Adobe Firefly. Specify style, lighting, composition, and mood with precision.',
    benefitKeys: ['toolImagePromptB1', 'toolImagePromptB2', 'toolImagePromptB3'],
    benefitFallbacks: [
      'Consistent visual output across tools',
      'Style and mood control',
      'Platform-optimized prompt syntax',
    ],
    useCaseKeys: ['toolImagePromptU1', 'toolImagePromptU2', 'toolImagePromptU3'],
    useCaseFallbacks: ['Product photography', 'Social media visuals', 'Book covers'],
    searchKeywords: ['image', 'prompt', 'midjourney', 'dall-e', 'stable diffusion'],
    emoji: '🎨',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    popular: true,
    badge: null,
  },
  {
    id: 'tool-video-prompt',
    route: '/create-page',
    categoryKey: 'catVideo',
    categoryFallback: 'Video',
    titleKey: 'toolVideoPromptTitle',
    titleFallback: 'Video Prompt Builder',
    descriptionKey: 'toolVideoPromptDesc',
    descriptionFallback:
      'Generate detailed video creation prompts for Runway, Pika, and Luma. Describe camera movement, scene transitions, lighting, and narrative arc.',
    benefitKeys: ['toolVideoPromptB1', 'toolVideoPromptB2', 'toolVideoPromptB3'],
    benefitFallbacks: [
      'Camera direction built-in',
      'Scene-by-scene structure',
      'Compatible with Runway Gen-3',
    ],
    useCaseKeys: ['toolVideoPromptU1', 'toolVideoPromptU2', 'toolVideoPromptU3'],
    useCaseFallbacks: ['YouTube content', 'Brand videos', 'Short films'],
    searchKeywords: ['video', 'prompt', 'runway', 'pika', 'luma'],
    emoji: '🎬',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    popular: true,
    badge: 'popular',
  },
  {
    id: 'tool-animation-prompt',
    route: '/create-page',
    categoryKey: 'catAnimation',
    categoryFallback: 'Animation',
    titleKey: 'toolAnimationTitle',
    titleFallback: 'Animation Prompt Builder',
    descriptionKey: 'toolAnimationDesc',
    descriptionFallback:
      'Build complete animation briefs including character movement, scene flow, timing cues, and style references for 2D, 3D, and motion graphics.',
    benefitKeys: ['toolAnimationB1', 'toolAnimationB2', 'toolAnimationB3'],
    benefitFallbacks: [
      'Frame-by-frame direction',
      'Character animation notes',
      'Style reference system',
    ],
    useCaseKeys: ['toolAnimationU1', 'toolAnimationU2', 'toolAnimationU3'],
    useCaseFallbacks: ['Explainer videos', 'Animated stories', 'Brand intros'],
    searchKeywords: ['animation', 'motion', '2d', '3d'],
    emoji: '✨',
    color: 'text-mint',
    bg: 'bg-mint/10',
    border: 'border-mint/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-voiceover',
    route: '/create-page',
    categoryKey: 'catVoice',
    categoryFallback: 'Voice',
    titleKey: 'toolVoiceoverTitle',
    titleFallback: 'Voiceover Creator',
    descriptionKey: 'toolVoiceoverDesc',
    descriptionFallback:
      'Write professional voiceover scripts with built-in pacing notes, emphasis markers, tone direction, and pronunciation guides. Ready for ElevenLabs or HeyGen.',
    benefitKeys: ['toolVoiceoverB1', 'toolVoiceoverB2', 'toolVoiceoverB3'],
    benefitFallbacks: [
      'Pacing and emphasis built-in',
      'Tone calibration',
      'Direct ElevenLabs export format',
    ],
    useCaseKeys: ['toolVoiceoverU1', 'toolVoiceoverU2', 'toolVoiceoverU3'],
    useCaseFallbacks: ['Product demos', 'Course narration', 'Podcast intros'],
    searchKeywords: ['voice', 'voiceover', 'elevenlabs', 'script'],
    emoji: '🎙️',
    color: 'text-secondary-foreground',
    bg: 'bg-secondary',
    border: 'border-border',
    popular: true,
    badge: 'popular',
  },
  {
    id: 'tool-character',
    route: '/create-page',
    categoryKey: 'catCharacters',
    categoryFallback: 'Characters',
    titleKey: 'toolCharacterTitle',
    titleFallback: 'Character Consistency Generator',
    descriptionKey: 'toolCharacterDesc',
    descriptionFallback:
      "Maintain visual and personality consistency for recurring characters across all your content. Perfect for children's books, branded mascots, and series content.",
    benefitKeys: ['toolCharacterB1', 'toolCharacterB2', 'toolCharacterB3'],
    benefitFallbacks: [
      'Visual consistency prompts',
      'Personality profile builder',
      'Cross-platform character sheets',
    ],
    useCaseKeys: ['toolCharacterU1', 'toolCharacterU2', 'toolCharacterU3'],
    useCaseFallbacks: ["Children's books", 'Brand mascots', 'Animated series'],
    searchKeywords: ['character', 'mascot', 'consistency'],
    emoji: '🧑‍🎨',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    popular: false,
    badge: 'new',
  },
  {
    id: 'tool-story',
    route: '/create-page',
    categoryKey: 'catWriting',
    categoryFallback: 'Writing',
    titleKey: 'toolStoryTitle',
    titleFallback: 'Story Generator',
    descriptionKey: 'toolStoryDesc',
    descriptionFallback:
      'Generate complete story structures with plot arcs, character development, scene breakdowns, and dialogue cues for any genre or age group.',
    benefitKeys: ['toolStoryB1', 'toolStoryB2', 'toolStoryB3'],
    benefitFallbacks: [
      '3-act structure builder',
      'Character arc mapping',
      'Genre-specific templates',
    ],
    useCaseKeys: ['toolStoryU1', 'toolStoryU2', 'toolStoryU3'],
    useCaseFallbacks: ["Children's books", 'Short stories', 'Script outlines'],
    searchKeywords: ['story', 'plot', 'writing', 'narrative'],
    emoji: '📖',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-storyboard',
    route: '/create-page',
    categoryKey: 'catVideo',
    categoryFallback: 'Video',
    titleKey: 'toolStoryboardTitle',
    titleFallback: 'Storyboard Creator',
    descriptionKey: 'toolStoryboardDesc',
    descriptionFallback:
      'Convert your script or concept into a detailed storyboard with shot descriptions, visual notes, timing, and camera angle guidance.',
    benefitKeys: ['toolStoryboardB1', 'toolStoryboardB2', 'toolStoryboardB3'],
    benefitFallbacks: [
      'Shot-by-shot breakdown',
      'Camera angle notation',
      'Timing and pacing guide',
    ],
    useCaseKeys: ['toolStoryboardU1', 'toolStoryboardU2', 'toolStoryboardU3'],
    useCaseFallbacks: ['Film production', 'Commercial shoots', 'YouTube videos'],
    searchKeywords: ['storyboard', 'shots', 'camera'],
    emoji: '🎞️',
    color: 'text-mint',
    bg: 'bg-mint/10',
    border: 'border-mint/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-product-ad',
    route: '/create-page',
    categoryKey: 'catMarketing',
    categoryFallback: 'Marketing',
    titleKey: 'toolProductAdTitle',
    titleFallback: 'Product Ad Generator',
    descriptionKey: 'toolProductAdDesc',
    descriptionFallback:
      'Create high-converting product advertisements with persuasive copy, visual direction, audience targeting notes, and platform-specific formatting.',
    benefitKeys: ['toolProductAdB1', 'toolProductAdB2', 'toolProductAdB3'],
    benefitFallbacks: [
      'AIDA framework built-in',
      'Platform-specific copy lengths',
      'A/B variant generation',
    ],
    useCaseKeys: ['toolProductAdU1', 'toolProductAdU2', 'toolProductAdU3'],
    useCaseFallbacks: ['E-commerce ads', 'Launch campaigns', 'Retargeting copy'],
    searchKeywords: ['product', 'ad', 'advertising', 'marketing'],
    emoji: '📣',
    color: 'text-secondary-foreground',
    bg: 'bg-secondary',
    border: 'border-border',
    popular: true,
    badge: 'popular',
  },
  {
    id: 'tool-reel',
    route: '/create-page',
    categoryKey: 'catSocial',
    categoryFallback: 'Social',
    titleKey: 'toolReelTitle',
    titleFallback: 'Reel Generator',
    descriptionKey: 'toolReelDesc',
    descriptionFallback:
      'Build viral-ready reel concepts with hook formulas, script structure, visual cues, trending audio suggestions, and CTA recommendations.',
    benefitKeys: ['toolReelB1', 'toolReelB2', 'toolReelB3'],
    benefitFallbacks: [
      'Proven hook formulas',
      'Trending format templates',
      'Platform-specific optimization',
    ],
    useCaseKeys: ['toolReelU1', 'toolReelU2', 'toolReelU3'],
    useCaseFallbacks: ['Instagram Reels', 'TikTok videos', 'YouTube Shorts'],
    searchKeywords: ['reel', 'tiktok', 'shorts', 'instagram'],
    emoji: '📱',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    popular: true,
    badge: 'popular',
  },
  {
    id: 'tool-thumbnail',
    route: '/create-page',
    categoryKey: 'catImages',
    categoryFallback: 'Images',
    titleKey: 'toolThumbnailTitle',
    titleFallback: 'Thumbnail Generator',
    descriptionKey: 'toolThumbnailDesc',
    descriptionFallback:
      'Design click-worthy thumbnail concepts with composition guidance, text overlay suggestions, color contrast tips, and emotion-driven visual direction.',
    benefitKeys: ['toolThumbnailB1', 'toolThumbnailB2', 'toolThumbnailB3'],
    benefitFallbacks: [
      'CTR-optimized composition',
      'Emotion and expression guides',
      'Text placement formulas',
    ],
    useCaseKeys: ['toolThumbnailU1', 'toolThumbnailU2', 'toolThumbnailU3'],
    useCaseFallbacks: ['YouTube thumbnails', 'Blog headers', 'Course covers'],
    searchKeywords: ['thumbnail', 'youtube', 'ctr'],
    emoji: '🖼️',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-hook',
    route: '/create-page',
    categoryKey: 'catWriting',
    categoryFallback: 'Writing',
    titleKey: 'toolHookTitle',
    titleFallback: 'Hook Generator',
    descriptionKey: 'toolHookDesc',
    descriptionFallback:
      'Generate scroll-stopping opening lines for videos, posts, emails, and ads. Uses proven psychological triggers and platform-specific hook formulas.',
    benefitKeys: ['toolHookB1', 'toolHookB2', 'toolHookB3'],
    benefitFallbacks: [
      '15+ hook formula types',
      'Platform-specific variants',
      'Emotion and curiosity triggers',
    ],
    useCaseKeys: ['toolHookU1', 'toolHookU2', 'toolHookU3'],
    useCaseFallbacks: ['Video openers', 'Email subject lines', 'Ad headlines'],
    searchKeywords: ['hook', 'headline', 'opener'],
    emoji: '🪝',
    color: 'text-mint',
    bg: 'bg-mint/10',
    border: 'border-mint/20',
    popular: false,
    badge: 'new',
  },
  {
    id: 'tool-caption',
    route: '/create-page',
    categoryKey: 'catSocial',
    categoryFallback: 'Social',
    titleKey: 'toolCaptionTitle',
    titleFallback: 'Caption Generator',
    descriptionKey: 'toolCaptionDesc',
    descriptionFallback:
      'Write engaging social media captions with hashtag strategy, emoji placement, CTA integration, and platform-specific length optimization.',
    benefitKeys: ['toolCaptionB1', 'toolCaptionB2', 'toolCaptionB3'],
    benefitFallbacks: [
      'Hashtag strategy included',
      'Platform length optimization',
      'CTA and engagement prompts',
    ],
    useCaseKeys: ['toolCaptionU1', 'toolCaptionU2', 'toolCaptionU3'],
    useCaseFallbacks: ['Instagram captions', 'LinkedIn posts', 'Facebook updates'],
    searchKeywords: ['caption', 'hashtag', 'social copy'],
    emoji: '✍️',
    color: 'text-secondary-foreground',
    bg: 'bg-secondary',
    border: 'border-border',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-assistant',
    route: '/create-page',
    categoryKey: 'catUtility',
    categoryFallback: 'Utility',
    titleKey: 'toolAssistantTitle',
    titleFallback: 'AI Assistant',
    descriptionKey: 'toolAssistantDesc',
    descriptionFallback:
      'Your always-on creative co-pilot. Ask anything about content strategy, platform best practices, AI tool usage, or get unstuck on any creative challenge.',
    benefitKeys: ['toolAssistantB1', 'toolAssistantB2', 'toolAssistantB3'],
    benefitFallbacks: [
      'Creative brainstorming',
      'Platform strategy advice',
      'Prompt troubleshooting',
    ],
    useCaseKeys: ['toolAssistantU1', 'toolAssistantU2', 'toolAssistantU3'],
    useCaseFallbacks: ['Strategy planning', 'Content ideation', 'Learning AI tools'],
    searchKeywords: ['assistant', 'chat', 'help', 'copilot'],
    emoji: '🤖',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    popular: false,
    badge: 'new',
  },
  {
    id: 'tool-prompt-enhancer',
    route: '/create-page',
    categoryKey: 'catUtility',
    categoryFallback: 'Utility',
    titleKey: 'toolPromptEnhancerTitle',
    titleFallback: 'Prompt Enhancer',
    descriptionKey: 'toolPromptEnhancerDesc',
    descriptionFallback:
      'Paste any basic prompt and watch AI transform it into a detailed, optimized version for your chosen tool. Works with any AI platform.',
    benefitKeys: ['toolPromptEnhancerB1', 'toolPromptEnhancerB2', 'toolPromptEnhancerB3'],
    benefitFallbacks: [
      'Universal prompt optimization',
      'Platform-specific enhancement',
      'Quality scoring before/after',
    ],
    useCaseKeys: ['toolPromptEnhancerU1', 'toolPromptEnhancerU2', 'toolPromptEnhancerU3'],
    useCaseFallbacks: [
      'Improving weak prompts',
      'Learning prompt structure',
      'Batch prompt upgrade',
    ],
    searchKeywords: ['prompt', 'enhancer', 'optimize'],
    emoji: '⚡',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-scene-builder',
    route: '/create-page',
    categoryKey: 'catVideo',
    categoryFallback: 'Video',
    titleKey: 'toolSceneBuilderTitle',
    titleFallback: 'Scene Builder',
    descriptionKey: 'toolSceneBuilderDesc',
    descriptionFallback:
      'Construct individual scenes with detailed visual descriptions, dialogue, action notes, lighting specs, and emotional direction for any medium.',
    benefitKeys: ['toolSceneBuilderB1', 'toolSceneBuilderB2', 'toolSceneBuilderB3'],
    benefitFallbacks: [
      'Scene-level granularity',
      'Lighting and mood specs',
      'Dialogue and action notes',
    ],
    useCaseKeys: ['toolSceneBuilderU1', 'toolSceneBuilderU2', 'toolSceneBuilderU3'],
    useCaseFallbacks: ['Film scenes', 'Animated sequences', 'Visual novel scenes'],
    searchKeywords: ['scene', 'dialogue', 'lighting'],
    emoji: '🎭',
    color: 'text-mint',
    bg: 'bg-mint/10',
    border: 'border-mint/20',
    popular: false,
    badge: null,
  },
  {
    id: 'tool-brand-kit',
    route: '/create-page',
    categoryKey: 'catMarketing',
    categoryFallback: 'Marketing',
    titleKey: 'toolBrandKitTitle',
    titleFallback: 'Brand Kit Generator',
    descriptionKey: 'toolBrandKitDesc',
    descriptionFallback:
      'Build a complete AI-powered brand kit including voice guidelines, visual style prompts, color palette descriptions, and content tone profiles.',
    benefitKeys: ['toolBrandKitB1', 'toolBrandKitB2', 'toolBrandKitB3'],
    benefitFallbacks: [
      'Brand voice documentation',
      'Visual consistency system',
      'Content tone framework',
    ],
    useCaseKeys: ['toolBrandKitU1', 'toolBrandKitU2', 'toolBrandKitU3'],
    useCaseFallbacks: ['Brand launches', 'Rebrand projects', 'Team alignment'],
    searchKeywords: ['brand', 'brand kit', 'identity'],
    emoji: '💎',
    color: 'text-secondary-foreground',
    bg: 'bg-secondary',
    border: 'border-border',
    popular: false,
    badge: 'new',
  },
  {
    id: 'tool-social-media-studio',
    route: '/social-media-studio',
    categoryKey: 'catSocial',
    categoryFallback: 'Social',
    titleKey: 'toolSocialStudioTitle',
    titleFallback: 'Social Media Studio',
    descriptionKey: 'toolSocialStudioDesc',
    descriptionFallback:
      'Generate complete social media content packages for Instagram, Facebook, TikTok, YouTube, Pinterest, LinkedIn, and X. Hook, caption, hashtags, image prompt, video script, and more.',
    benefitKeys: ['toolSocialStudioB1', 'toolSocialStudioB2', 'toolSocialStudioB3'],
    benefitFallbacks: [
      '7 platforms supported',
      'Hook + Caption + CTA + Hashtags',
      'AI image prompt & video script',
    ],
    useCaseKeys: ['toolSocialStudioU1', 'toolSocialStudioU2', 'toolSocialStudioU3'],
    useCaseFallbacks: ['Instagram Reels', 'LinkedIn posts', 'TikTok content'],
    searchKeywords: ['social', 'instagram', 'tiktok', 'linkedin'],
    emoji: '📱',
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/20',
    popular: true,
    badge: 'new',
  },
  {
    id: 'tool-svg-studio',
    route: '/svg-studio',
    categoryKey: 'catImages',
    categoryFallback: 'Images',
    titleKey: 'toolSvgStudioTitle',
    titleFallback: 'SVG Studio',
    descriptionKey: 'toolSvgStudioDesc',
    descriptionFallback:
      'Create clean vector SVG artwork for stickers, logos, Cricut cut files, coloring pages, and print-ready graphics.',
    benefitKeys: ['toolSvgStudioB1', 'toolSvgStudioB2', 'toolSvgStudioB3'],
    benefitFallbacks: [
      'Vector SVG output',
      'Logos, stickers & cut files',
      'Saves to My Library',
    ],
    useCaseKeys: ['toolSvgStudioU1', 'toolSvgStudioU2', 'toolSvgStudioU3'],
    useCaseFallbacks: ['Logo icons', 'Sticker designs', 'Cricut cut files'],
    searchKeywords: [
      // EN
      'svg',
      'vector',
      'cricut',
      'cut file',
      'cut files',
      'scalable',
      'logo',
      'icon',
      'icons',
      'sticker',
      // PT
      'vetor',
      'vetorial',
      'arquivo de corte',
      'arquivos de corte',
      'escalável',
      'escalavel',
      'adesivo',
      'ícone',
      'icone',
      // ES
      'vectorial',
      'archivo de corte',
      'archivos de corte',
      'escalable',
      'icono',
      'logotipo',
      'pegatina',
    ],
    emoji: '',
    lucideIcon: 'FileCode',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/20',
    popular: true,
    badge: 'new',
  },
] as const;

/** Ordered IDs derived from the runtime definitions (never a separate list). */
export const AI_TOOL_CARD_IDS = AI_TOOLS.map((tool) => tool.id);

export type AiToolCardId = (typeof AI_TOOLS)[number]['id'];

/** Visible AI Tools card count — always from the runtime definition list. */
export function getAiToolsVisibleCount(): number {
  return AI_TOOLS.length;
}

export function getAiToolRoute(toolId: string): string {
  const tool = AI_TOOLS.find((entry) => entry.id === toolId);
  return tool?.route ?? '/create-page';
}

function pickLocale(
  at: AiToolLocaleBucket,
  key: string,
  fallback: string
): string {
  const value = at?.[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

/** Build the exact cards rendered by the AI Tools grid. */
export function buildVisibleAiTools(at: AiToolLocaleBucket): AiToolCard[] {
  return AI_TOOLS.map((tool) => {
    let badge: string | null = null;
    if (tool.badge === 'new') {
      badge = pickLocale(at, 'badgeNew', 'New');
    } else if (tool.badge === 'popular') {
      badge = pickLocale(at, 'badgePopular', 'Popular');
    }

    return {
      id: tool.id,
      route: tool.route,
      title: pickLocale(at, tool.titleKey, tool.titleFallback),
      category: pickLocale(at, tool.categoryKey, tool.categoryFallback),
      badge,
      description: pickLocale(at, tool.descriptionKey, tool.descriptionFallback),
      benefits: tool.benefitKeys.map((key, index) =>
        pickLocale(at, key, tool.benefitFallbacks[index])
      ),
      useCases: tool.useCaseKeys.map((key, index) =>
        pickLocale(at, key, tool.useCaseFallbacks[index])
      ),
      searchKeywords: [...tool.searchKeywords],
      emoji: tool.emoji,
      lucideIcon: tool.lucideIcon,
      color: tool.color,
      bg: tool.bg,
      border: tool.border,
      popular: tool.popular,
    };
  });
}

export function toolMatchesSearch(tool: AiToolCard, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return true;

  const haystack = [
    tool.title,
    tool.description,
    tool.category,
    ...tool.benefits,
    ...tool.useCases,
    ...tool.searchKeywords,
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
}

export function filterVisibleAiTools(
  tools: AiToolCard[],
  options: { search?: string; categoryLabel?: string | null; isAllCategory?: boolean }
): AiToolCard[] {
  const isAll = options.isAllCategory ?? true;
  const categoryLabel = options.categoryLabel ?? null;
  const search = options.search ?? '';

  return tools.filter((tool) => {
    const matchCat = isAll || tool.category === categoryLabel;
    const matchSearch = toolMatchesSearch(tool, search);
    return matchCat && matchSearch;
  });
}

export function formatAiToolsHeaderSubtitle(
  template: string | undefined,
  count: number,
  fallbackEn: string
): string {
  const base =
    template && template.includes('{count}')
      ? template
      : fallbackEn.includes('{count}')
        ? fallbackEn
        : '{count} specialized AI tools for every creative workflow. Launch any tool to start generating.';
  return base.replace(/\{count\}/g, String(count));
}

export function formatAiToolsShowingOf(
  template: string | undefined,
  shown: number,
  total: number
): string {
  const base = template?.includes('{shown}') && template.includes('{total}')
    ? template
    : 'Showing {shown} of {total} tools';
  return base
    .replace(/\{shown\}/g, String(shown))
    .replace(/\{total\}/g, String(total));
}
